import { useEffect, useState } from "react";
import { apiClient } from "../../../api/apiClient";
import { reservarTurno } from "../../../api/apiResources";
import { getEntityId } from "../../layout/paciente/patientHelpers";
import PasoTipo from "./PasoTipo";
import PasoCriterio from "./PasoCriterio";
import PasoFecha from "./PasoFecha";
import PasoTurno from "./PasoTurno";
import WizardSteps from "./WizardSteps";
import ConfirmarReserva from "./ConfirmarReserva";
import {
    mapearTurno,
    formatearFecha,
    formatearHora,
    filtrarFuturos,
    derivarMedicos,
    derivarServicios,
    filtrarPorCriterio,
    derivarFechas,
    filtrarPorFecha,
} from "./buscarTurnoHelpers";
import "./BuscarTurno.css";

const BuscarTurno = ({ paciente, onReservar }) => {
    // Plan del paciente para calcular coberturas (puede no tener).
    const plan = paciente?.plan ?? null;

    // Datos (una sola llamada al backend).
    const [disponibles, setDisponibles] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [errorCarga, setErrorCarga] = useState("");

    // Estado del wizard.
    const [paso, setPaso] = useState(1);
    const [tipo, setTipo] = useState(null);
    const [termino, setTermino] = useState("");
    const [medicoSel, setMedicoSel] = useState(null);
    const [servicioSel, setServicioSel] = useState(null);
    const [fechaSel, setFechaSel] = useState(null);

    // Reserva.
    const [turnoAConfirmar, setTurnoAConfirmar] = useState(null);
    const [reservando, setReservando] = useState(false);
    const [errorReserva, setErrorReserva] = useState("");
    const [exito, setExito] = useState("");

    // Carga inicial: unica llamada al backend (limit=500 para traer todos).
    useEffect(() => {
        const cargar = async () => {
            setCargando(true);
            setErrorCarga("");
            try {
                const res = await apiClient.get("/turnos/disponibles?limit=500");
                const data = Array.isArray(res?.data) ? res.data : [];
                setDisponibles(data.map(mapearTurno));
            } catch (e) {
                setErrorCarga("No se pudieron cargar los turnos disponibles.");
            } finally {
                setCargando(false);
            }
        };
        cargar();
    }, []);

    // Datos derivados para cada paso (la logica vive en buscarTurnoHelpers).
    const futuros = filtrarFuturos(disponibles);
    const medicos = derivarMedicos(futuros, termino);
    const servicios = derivarServicios(futuros);
    const turnosCriterio = filtrarPorCriterio(futuros, tipo, medicoSel, servicioSel);
    const fechas = derivarFechas(turnosCriterio);
    const turnosFecha = filtrarPorFecha(turnosCriterio, fechaSel);

    // ── Handlers ──
    const reiniciar = () => {
        setPaso(1);
        setTipo(null);
        setTermino("");
        setMedicoSel(null);
        setServicioSel(null);
        setFechaSel(null);
        setTurnoAConfirmar(null);
        setErrorReserva("");
    };

    const elegirTipo = (nuevoTipo) => {
        setTipo(nuevoTipo);
        setTermino("");
        setMedicoSel(null);
        setServicioSel(null);
        setPaso(2);
    };

    const elegirMedico = (medico) => {
        setMedicoSel(medico);
        setFechaSel(null);
        setPaso(3);
    };

    const elegirServicio = (servicio) => {
        setServicioSel(servicio);
        setFechaSel(null);
        setPaso(3);
    };

    const elegirFecha = (clave) => {
        setFechaSel(clave);
        setPaso(4);
    };

    const volver = () => {
        setErrorReserva("");
        if (paso === 2) {
            setTipo(null);
            setPaso(1);
        } else if (paso === 3) {
            setMedicoSel(null);
            setServicioSel(null);
            setPaso(2);
        } else if (paso === 4) {
            setFechaSel(null);
            setPaso(3);
        }
    };

    const confirmarReserva = async () => {
        if (!turnoAConfirmar || !paciente) return;
        setReservando(true);
        setErrorReserva("");
        try {
            await reservarTurno(turnoAConfirmar.id, getEntityId(paciente));
            // Sacamos el turno reservado del listado local.
            setDisponibles((prev) => prev.filter((t) => t.id !== turnoAConfirmar.id));
            setExito(
                `Turno reservado con ${turnoAConfirmar.medicoNombre} el ${formatearFecha(turnoAConfirmar.fechaHora)} a las ${formatearHora(turnoAConfirmar.fechaHora)}.`
            );
            setTurnoAConfirmar(null);
            onReservar?.();
            reiniciar();
        } catch (e) {
            setErrorReserva(e.message || "No se pudo reservar el turno. Puede que ya haya sido tomado.");
        } finally {
            setReservando(false);
        }
    };

    // ── Render ──
    if (cargando) {
        return (
            <div className="patient-section">
                <div className="patient-state" role="status" aria-busy="true">
                    <div className="patient-state__loader" aria-hidden="true" />
                    <p>Cargando turnos disponibles...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="patient-section">
            <div className="patient-section__header">
                <div>
                    <p className="eyebrow">Agenda</p>
                    <h2>Reservar turno</h2>
                </div>
            </div>

            <WizardSteps paso={paso} />

            {exito && (
                <div className="success-banner" role="alert" aria-live="assertive">
                    <strong>Turno reservado exitosamente</strong>
                    <span>{exito}</span>
                    <button className="success-banner__close" aria-label="Cerrar" onClick={() => setExito("")}>✕</button>
                </div>
            )}

            {errorCarga && <div className="patient-state patient-state--error" role="alert">{errorCarga}</div>}

            {!paciente && (
                <div className="patient-state patient-state--error" role="alert">
                    No se encontró un paciente asociado al usuario logueado.
                </div>
            )}

            {paso > 1 && (
                <div className="wizard-nav">
                    <button type="button" className="btn-ghost" onClick={volver}>← Volver</button>
                    <button type="button" className="btn-ghost" onClick={reiniciar}>Empezar de nuevo</button>
                </div>
            )}

            {paso === 1 && <PasoTipo onElegir={elegirTipo} />}

            {paso === 2 && (
                <PasoCriterio
                    tipo={tipo}
                    termino={termino}
                    onTermino={setTermino}
                    medicos={medicos}
                    servicios={servicios}
                    onElegirMedico={elegirMedico}
                    onElegirServicio={elegirServicio}
                />
            )}

            {paso === 3 && <PasoFecha fechas={fechas} onElegir={elegirFecha} />}

            {paso === 4 && (
                <PasoTurno
                    turnos={turnosFecha}
                    plan={plan}
                    onReservar={setTurnoAConfirmar}
                    deshabilitado={!paciente}
                />
            )}

            {turnoAConfirmar && (
                <ConfirmarReserva
                    turno={turnoAConfirmar}
                    error={errorReserva}
                    reservando={reservando}
                    onCancelar={() => setTurnoAConfirmar(null)}
                    onConfirmar={confirmarReserva}
                />
            )}
        </div>
    );
};

export default BuscarTurno;
